<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"
import { computed, ref, watch, useTemplateRef } from "vue"
import { SOURCES_TYPES, GRID_OFFER_CODES } from "@core/constants"
import { useRebuildDataTask, useSuppliers } from "@core/composables"
import { useSourceStore } from "@core/stores/source.store"
import { parseFloatSafety } from "@core/helpers"
import { SourceService } from "@core/services"
import { OldOffrePce, OldOffrePdl, TypeOfferForm } from "@types"

const sourceService = new SourceService()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            button_validate: "Valider",
            button_quit: "Annuler",
            title: "Modification des informations tarifaires et contractuelles du compteur",
            supplier: {
                label: "Fournisseur",
                placeholder: "Choisir un fournisseur",
            },
            end_date_contract: "Date de fin de contrat",
        },
    },
})

type Props = {
    meterId: string
    type: string
    visible?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    visible: false,
})

const [modelVisible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

const errorMessage = ref("")

const sourceStore = useSourceStore()
const { launchRebuildDataTask } = useRebuildDataTask({
    taskname: "Récupération des données du compteur",
})
const { getSuppliers, isLoadingSuppliers } = useSuppliers()

const form = ref<any>(null)
const typeOffer = ref<TypeOfferForm | null>(null)
// référence vers le composant FormPricing
const formPricing = useTemplateRef("formPricing")

// ================================
// COMPUTED
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})
const getGrilleByType = computed(() => {
    const source = getSourceById.value

    const type = getSourceById.value?.type as "pdl" | "pce"

    if (!type) return null

    return source?.informations?.[type]?.structure_tarifaire?.offre || null
})

const loading = ref(false)

/**
 * Récupère le bon format du prix selon la grille tarifaire pour un compteur PDL
 * @param form - les valeurs du formulaire en fonction du type d'offre
 * @param offerType - le type d'offre (Base, HPHC, Tempo, Autre)
 */
const getGrilleTarifairePricing = computed(() => {
    switch (typeOffer.value) {
        case GRID_OFFER_CODES.BASE:
            return [
                {
                    grille: "Base",
                    prix_kwh: parseFloatSafety(form.value.tarifBase / 100),
                },
            ]
        case GRID_OFFER_CODES.HPHC:
            if (getSegment.value === "C4") {
                return [
                    {
                        grille: "HPH",
                        prix_kwh: parseFloatSafety(form.value?.tarifHPH / 100),
                    },
                    {
                        grille: "HCH",
                        prix_kwh: parseFloatSafety(form.value?.tarifHCH / 100),
                    },
                    {
                        grille: "HPB",
                        prix_kwh: parseFloatSafety(form.value?.tarifHPB / 100),
                    },
                    {
                        grille: "HCB",
                        prix_kwh: parseFloatSafety(form.value?.tarifHCB / 100),
                    },
                ]
            } else {
                return [
                    {
                        grille: "HP",
                        prix_kwh: parseFloatSafety(form.value?.tarifHP / 100),
                    },
                    {
                        grille: "HC",
                        prix_kwh: parseFloatSafety(form.value?.tarifHC / 100),
                    },
                ]
            }
        case GRID_OFFER_CODES.TEMPO:
            return [
                {
                    grille: "BUHP",
                    prix_kwh: parseFloatSafety(form.value?.tarifBUHP / 100),
                },
                {
                    grille: "BUHC",
                    prix_kwh: parseFloatSafety(form.value?.tarifBUHC / 100),
                },
                {
                    grille: "BCHP",
                    prix_kwh: parseFloatSafety(form.value?.tarifBCHP / 100),
                },
                {
                    grille: "BCHC",
                    prix_kwh: parseFloatSafety(form.value?.tarifBCHC / 100),
                },
                {
                    grille: "RHP",
                    prix_kwh: parseFloatSafety(form.value?.tarifRHP / 100),
                },
                {
                    grille: "RHC",
                    prix_kwh: parseFloatSafety(form.value?.tarifRHC / 100),
                },
            ]
        default:
            return [
                {
                    grille: "Autre",
                    prix_kwh: parseFloatSafety(form.value.tarifMoyen / 100),
                },
            ]
    }
})

/**
 * Quand l'utilisateur sauvegarde ses informations
 * @return {Promise<void>}
 */
const onSubmit = async () => {
    errorMessage.value = ""
    try {
        loading.value = true

        const body = {
            offer: {
                prix_abonnement: parseFloatSafety(form.value.prixAbonnement),
            },
        } as any

        if (formOptional.value._id_supplier === "other") {
            body.offer._id_energy_supplier = null
        } else {
            body.offer._id_energy_supplier = formOptional.value._id_supplier
        }

        // si c'est un compteur PDL
        if (getSourceById?.value?.type === SOURCES_TYPES.PDL) {
            body.offer = {
                ...body.offer,
                grille_tarifaire: typeOffer.value,
                prix_grille_tarifaire: getGrilleTarifairePricing.value,
            }
        }
        // si c'est un compteur PCE
        else {
            body.offer = {
                ...body.offer,
                prix_kwh: parseFloatSafety(form.value?.tarifMoyen / 100),
                cta_annuelle: form.value?.cta_annuelle,
            }
        }
        await sourceService.updateMeterById(props.meterId, body)

        // Fetch la source
        await sourceStore.fetchSourceById({ _id_source: props.meterId })

        // DEV-527 — un changement d'offre grdf (PCE) ne re-collecte PAS auprès de
        // GRDF (parcimonie). Le reprice borné aux dates de contrat est traité côté
        // serveur (pricer, C2). On ne lance donc le rebuild de collecte que pour
        // les autres compteurs.
        if (getSourceById.value?.type !== SOURCES_TYPES.PCE) {
            launchRebuildDataTask(props.meterId)
        }

        // ferme la modal
        modelVisible.value = false
    } catch (err) {
        const error = err as Error
        errorMessage.value = error?.message
    } finally {
        loading.value = false
    }
}

const formOptional = ref<any>({
    _id_supplier: null,
})

const getSupplierOptions = computed(() => {
    if (isLoadingSuppliers.value) return []

    const suppliersList = getSuppliers.value ?? []
    const sortedSuppliers = [...suppliersList].sort((a, b) =>
        a.name === b.name ? 0 : a.name > b.name ? 1 : -1
    )

    return [
        {
            code: "other",
            name: "Autre",
            img: null,
            _id_supplier: "",
        },
        ...sortedSuppliers,
    ]
})

function onUpdateForm({
    form: newForm,
    typeOffer: newTypeOffer,
}: {
    form: any
    typeOffer: TypeOfferForm | null
}) {
    form.value = newForm
    typeOffer.value = newTypeOffer
}

const getSegment = computed(() => {
    const segment = ""
    if (getSourceById?.value?.informations?.pdl) {
        return getSourceById?.value?.informations?.pdl?.compteur?.segment
    }
    return segment
})

// ================================
// WATCHERS
// ================================
watch(
    () => getSourceById.value,
    () => {
        form.value = initialFormPricing(getGrilleByType.value)
        formOptional.value = initialFormOptional(getGrilleByType.value)
    },
    {
        immediate: true,
        deep: true,
    }
)

type Form = {
    prixAbonnement: number | null
    tarifBase: number | null
    tarifMoyen: number | null
    cta_annuelle: number | null
    hphc: {
        tarifHP: number | null
        tarifHC: number | null
    }
    hphcC4: {
        tarifHPH: number | null
        tarifHCH: number | null
        tarifHPB: number | null
        tarifHCB: number | null
    }
    tempo: {
        tarifBUHP: number | null
        tarifBUHC: number | null
        tarifBCHP: number | null
        tarifBCHC: number | null
        tarifRHP: number | null
        tarifRHC: number | null
    }
}

/**
 * Map les informations de la prop "meter" avec le formulaire
 * @param value
 */
function initialFormPricing(value: OldOffrePdl | OldOffrePce | null) {
    // remplie le formulaire des prix
    const innerForm = {
        // prix de l'abonnement
        prixAbonnement: value?.prix_abonnement,
        // tarif base (catégorie Base)
        tarifBase: null,
        // tarif moyen (catégorie Autre)
        tarifMoyen: null,
        // Contribution Tarifaire d'Acheminement (CTA) annuelle
        cta_annuelle: (value as OldOffrePce)?.cta_annuelle,
        // tarif heures pleines / creuses (catégorie HP/HC C5)
        hphc: {
            // tarif heures pleines
            tarifHP: null,
            // tarif heures creuses
            tarifHC: null,
        },
        // tarif heures pleines / creuses - haute / basse saison (catégorie HP/HC C4)
        hphcC4: {
            // tarif heures pleines haute saison
            tarifHPH: null,
            // tarif heures creuses haute saison
            tarifHCH: null,
            // tarif heures pleines basse saison
            tarifHPB: null,
            // tarif heures creuses basse saison
            tarifHCB: null,
        },
        // tarif tempo (catégorie Tempo)
        tempo: {
            // tarif tempo jour bleu heures pleines
            tarifBUHP: null,
            // tarif tempo jour bleu heures creuses
            tarifBUHC: null,
            // tarif tempo jour blanc heures pleines
            tarifBCHP: null,
            // tarif tempo jour blanc heures creuses
            tarifBCHC: null,
            // tarif tempo jour rouge heures pleines
            tarifRHP: null,
            // tarif tempo jour rouge heures creuses
            tarifRHC: null,
        },
    } as Form

    // PCE
    if (getSourceById.value?.type === SOURCES_TYPES.PCE) {
        innerForm.tarifMoyen =
            transformPriceToCentimes((value as OldOffrePce)?.prix_kwh) || null
        innerForm.cta_annuelle = (value as OldOffrePce)?.cta_annuelle || null

        return innerForm
    }

    value = value as OldOffrePdl

    // offre base
    if (value?.grille_tarifaire === GRID_OFFER_CODES.BASE)
        innerForm.tarifBase = transformPriceToCentimes(
            value?.prix_grille_tarifaire?.[0]?.prix_kwh
        )
    // offre HPHC C4
    else if (
        value?.grille_tarifaire === GRID_OFFER_CODES.HPHC &&
        ["C4", "P3"].includes(getSegment.value)
    ) {
        innerForm.hphcC4 = {
            tarifHPH: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[0]?.prix_kwh
            ),
            tarifHCH: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[1]?.prix_kwh
            ),
            tarifHPB: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[2]?.prix_kwh
            ),
            tarifHCB: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[3]?.prix_kwh
            ),
        }
    }
    // offre HPHC
    else if (value?.grille_tarifaire === GRID_OFFER_CODES.HPHC) {
        innerForm.hphc = {
            tarifHP: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[0]?.prix_kwh
            ),
            tarifHC: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[1]?.prix_kwh
            ),
        }
    }
    // offre Tempo
    else if (value?.grille_tarifaire === GRID_OFFER_CODES.TEMPO) {
        innerForm.tempo = {
            tarifBUHP: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[0]?.prix_kwh
            ),
            tarifBUHC: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[1]?.prix_kwh
            ),
            tarifBCHP: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[2]?.prix_kwh
            ),
            tarifBCHC: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[3]?.prix_kwh
            ),
            tarifRHP: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[4]?.prix_kwh
            ),
            tarifRHC: transformPriceToCentimes(
                value?.prix_grille_tarifaire?.[5]?.prix_kwh
            ),
        }
    }

    // Autre
    if (value?.grille_tarifaire === GRID_OFFER_CODES.OTHER) {
        innerForm.tarifMoyen = transformPriceToCentimes(
            value?.prix_grille_tarifaire?.[0]?.prix_kwh
        )
    }
    return innerForm
}

function initialFormOptional(value: OldOffrePce | OldOffrePdl | null) {
    // remplie le formulaire optionnel
    return {
        _id_supplier: value?._id_energy_supplier || null,
    }
}

function transformPriceToCentimes(price: number | null) {
    if (price === null || isNaN(price)) return null
    return Number.parseFloat((price * 100).toFixed(2))
}

const getCurrentSupplierSelected = computed(() => {
    const suppliersList = getSuppliers.value ?? []
    return suppliersList.find(
        (s) => s._id_supplier === formOptional.value._id_supplier
    )
})
</script>

<template>
    <BaseDialog
        v-model:visible="modelVisible"
        modal
        :closable="true"
        dismissable-mask
        class="modal-meter-edit-offer max-w-[50rem] w-full"
        :pt="{
            header: 'flex-col-reverse p-0!',
            headeractions: 'ml-auto',
            title: 'text-primary mb-4',
        }"
        :header="t('title')"
    >
        <div class="flex flex-col items-left w-full h-full">
            <div class="flex flex-col gap-6">
                <FormSourcePricing
                    ref="formPricing"
                    :meter-id="meterId"
                    :type="type"
                    :value="form"
                    @update:form="onUpdateForm"
                />
                <div class="flex flex-col gap-6">
                    <!-- Fournisseurs -->
                    <div class="flex flex-col gap-2">
                        <BaseInputWrapper
                            input-id="supplier"
                            :label="t('supplier.label')"
                            unfloat
                        >
                            <BaseSelect
                                id="supplier"
                                v-model="formOptional._id_supplier"
                                option-label="name"
                                option-value="_id_supplier"
                                :options="getSupplierOptions"
                                show-clear
                                :placeholder="
                                    t('supplier.placeholder').toString()
                                "
                            >
                                <template #value>
                                    <div
                                        class="flex gap-2 items-center"
                                        v-if="getCurrentSupplierSelected"
                                    >
                                        <div
                                            class="w-6 h-6 rounded-md flex items-center justify-center text-sm font-light text-gray-700"
                                            :class="
                                                !getCurrentSupplierSelected?.img
                                                    ? 'bg-gray-200'
                                                    : 'bg-transparent'
                                            "
                                        >
                                            <span
                                                v-if="
                                                    !getCurrentSupplierSelected?.img
                                                "
                                                >{{
                                                    getCurrentSupplierSelected?.name !==
                                                    "Autre"
                                                        ? getCurrentSupplierSelected?.name?.charAt(
                                                              0
                                                          )
                                                        : "F"
                                                }}</span
                                            >
                                            <img
                                                v-else-if="
                                                    getCurrentSupplierSelected?.img
                                                "
                                                :src="
                                                    getCurrentSupplierSelected?.img
                                                "
                                                :alt="
                                                    getCurrentSupplierSelected?.name
                                                "
                                                class="w-7 h-7 rounded-md object-contain"
                                            />
                                        </div>
                                        <span class="ml-2">{{
                                            getCurrentSupplierSelected?.name
                                        }}</span>
                                    </div>
                                </template>
                                <template #option="{ option }">
                                    <div class="flex items-center gap-2">
                                        <!-- Logo du fournisseur -->
                                        <div
                                            class="w-6 h-6 rounded-md flex items-center justify-center text-sm font-light text-gray-700"
                                            :class="
                                                !option?.img
                                                    ? 'bg-gray-200'
                                                    : 'bg-transparent'
                                            "
                                        >
                                            <span v-if="!option?.img">{{
                                                option.name !== "Autre"
                                                    ? option.name.charAt(0)
                                                    : "F"
                                            }}</span>
                                            <img
                                                v-else-if="option?.img"
                                                :src="option.img"
                                                :alt="option.name"
                                                class="w-7 h-7 rounded-md object-contain"
                                            />
                                        </div>
                                        <span>{{ option.name }}</span>
                                    </div>
                                </template>
                            </BaseSelect>
                        </BaseInputWrapper>
                    </div>
                </div>
            </div>
        </div>
        <template #footer>
            <div class="flex justify-between w-full">
                <BaseButton
                    @click="modelVisible = false"
                    :label="t('button_quit')"
                    text
                    severity="secondary"
                />
                <BaseButton
                    @click="onSubmit"
                    :label="t('button_validate')"
                    severity="primary"
                    :loading="loading"
                    :disabled="!formPricing?.valid"
                    autofocus
                />
            </div>
        </template>
    </BaseDialog>
</template>

<style scoped></style>
